import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { instances, type AuftragAngelegt } from '../api';
import type { Instance } from '../types';

/**
 * Eine Instanz und ihre Container entfernen.
 *
 * Getrennt vom übrigen Aktionsband und mit eigener Bestätigung, weil es die
 * einzige Aktion hier ist, die nichts zurücklässt, was man wieder anschalten
 * könnte. Ein `confirm()` wie bei „Stop" wäre zu wenig: das klickt man weg.
 *
 * Getippt werden muss der Stackname. Nicht als Zeremonie, sondern weil man ihn
 * dabei liest — und weil die Instanzseiten einander gleichen.
 *
 * Die Bestätigung prüft auch der Server. Was unwiederbringlich ist, darf nicht
 * daran hängen, dass die Oberfläche mitspielt.
 */
export default function InstanzEntfernenModal({
  instanz,
  onSchliessen,
  onEingereiht,
}: {
  instanz: Instance;
  onSchliessen: () => void;
  onEingereiht: (auftrag: AuftragAngelegt) => void;
}) {
  const { t } = useTranslation();
  const erwartet = instanz.stackName ?? instanz.id;
  const [getippt, setGetippt] = useState('');
  const [datenLoeschen, setDatenLoeschen] = useState(false);
  const [fehler, setFehler] = useState('');

  const entfernen = useMutation({
    mutationFn: () => instances.entfernen(instanz.id, getippt.trim(), datenLoeschen),
    onSuccess: onEingereiht,
    onError: (err: unknown) =>
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t('remove.failed'),
      ),
  });

  const passt = getippt.trim() === erwartet;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-600" />
            <div>
              <h2 className="text-base font-semibold text-slate-800">{t('remove.title')}</h2>
              <p className="mt-0.5 text-xs text-slate-500">{t('remove.subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {/* Was passiert — in der Reihenfolge, in der es passiert. */}
          <ul className="space-y-1 text-xs text-slate-600">
            <li>• {t('remove.stepDown')}</li>
            <li>• {t('remove.stepDir')}</li>
            <li>• {t('remove.stepDomain')}</li>
            <li>• {t('remove.stepRecord')}</li>
          </ul>

          {/* Bind-Mounts. Der Punkt, an dem sich „geloescht" und „weg" trennen. */}
          <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <input
              type="checkbox"
              checked={datenLoeschen}
              onChange={(e) => setDatenLoeschen(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs">
              <span className="font-medium text-slate-700">{t('remove.dataLabel')}</span>
              <span className="mt-0.5 block text-slate-500">{t('remove.dataHint')}</span>
            </span>
          </label>

          {datenLoeschen && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {t('remove.dataWarning')}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs text-slate-600">
              {t('remove.typeToConfirm')}{' '}
              <code className="rounded bg-slate-100 px-1 font-mono text-slate-800">{erwartet}</code>
            </label>
            <input
              value={getippt}
              onChange={(e) => setGetippt(e.target.value)}
              autoFocus
              spellCheck={false}
              className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          {fehler && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{fehler}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!passt || entfernen.isPending}
            onClick={() => entfernen.mutate()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-40"
          >
            <Trash2 size={14} />
            {entfernen.isPending ? t('remove.running') : t('remove.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
